/**
 * „À la carte · Abrechnung vor Ort“ (Migration 731, Antoine 19.10.2026, docs/PLAN-A-LA-CARTE.md).
 *
 * Eine Variante kann bewusst ohne Betrag verschickt werden: die Gäste bestellen im Restaurant von
 * der Karte. Hier stehen NUR die Texte und zwei kleine Entscheidungen der Oberfläche — Beträge
 * werden nie gerechnet (Leitprinzip: Beträge nur aus `packages/pricing`), der Server entscheidet
 * über Versand, Annahme und Zahlung.
 *
 * Deutsche Anführungszeichen: öffnend „ (U+201E), schließend “ (U+201C), nie das gerade ".
 */

export const A_LA_CARTE_SCHALTER = "À la carte · Abrechnung vor Ort";
export const A_LA_CARTE_ERKLAERUNG =
  "Die Gäste bestellen im Restaurant von der Karte. Das Angebot hat keinen Betrag; keine Anzahlung, kein Zahlungsplan, keine Rechnung aus MAESTRO.";
/** Anstelle eines Betrags (Variantenkarte, Kanban, Verlauf, Zusammenfassung). */
export const NACH_KARTE = "Nach Karte";
export const A_LA_CARTE_VOR_ORT = "à la carte · vor Ort";
export const A_LA_CARTE_KLEIN = "à la carte";
export const A_LA_CARTE_KONDITIONEN_ENTFAELLT = "Entfällt";
export const A_LA_CARTE_MIT_BETRAG_FEHLER = "Eine À-la-carte-Variante darf keinen Betrag haben.";
/** sql/770 („Alte Fassung kurz sperren“): nach dem Umschalten im Status „gesendet“, bis zum Neuversand. */
export const FASSUNG_NEU_SENDEN = "Geändert – bitte neu senden. Bis dahin kann der Gast nicht annehmen.";
/** Option 2 (03.11.2026): Bestätigung beim Umstellen AUF à la carte, wenn die Variante Positionen hat. */
export const A_LA_CARTE_UMSTELLEN_TITEL = "Auf à la carte umstellen?";
export function aLaCarteUmstellenText(anzahl: number): string {
  const pos = anzahl === 1 ? "Die 1 Position dieser Variante wird" : `Die ${anzahl} Positionen dieser Variante werden`;
  return `Die Gäste bestellen vor Ort von der Karte. ${pos} dafür entfernt. Das lässt sich nicht automatisch wiederherstellen.`;
}

/** Hat die Variante das Kennzeichen? (Backoffice-Form, camelCase.) */
export function istALaCarteOption(o: { aLaCarte?: boolean | null } | null | undefined): boolean {
  return o?.aLaCarte === true;
}

/**
 * Fehlertext für den Versand: der Server antwortet 422 `a_la_carte_mit_betrag`, wenn eine
 * gekennzeichnete Variante doch einen Betrag hat. Wir sagen das zuerst in einem Satz und hängen die
 * Server-Meldung (nennt die Variante und den Ausweg) an.
 */
export function aLaCarteFehlertext(e: unknown): string | null {
  const code = (e as { code?: string } | null)?.code;
  if (code !== "a_la_carte_mit_betrag") return null;
  const detail = e instanceof Error ? e.message : "";
  return `NICHT versendet: ${A_LA_CARTE_MIT_BETRAG_FEHLER}${detail ? ` ${detail}` : ""}`;
}

/** Abzeichen im Vorgangskopf. */
export const A_LA_CARTE_VOR_ORT_KOPF = "À la carte · vor Ort";

/**
 * Migration 731: „À la carte“ steht in der Tab-Leiste am Ende (kein Preis, der sie einordnet).
 * Stabile Teilung, die Reihenfolge innerhalb der beiden Gruppen bleibt.
 */
export function aLaCarteAnsEnde<T extends { aLaCarte?: boolean | null }>(varianten: readonly T[]): T[] {
  return [...varianten.filter((o) => !istALaCarteOption(o)), ...varianten.filter((o) => istALaCarteOption(o))];
}

/** Sprachen, in denen die Gastseite „À la carte“ kennt (DE/EN heute auf der Seite, IT/FR für Widget und künftige Seite). */
export type ALaCarteSprache = "de" | "en" | "it" | "fr";

/**
 * Gastseite (Sie-Form) für eine „À la carte“-Variante: kein Betrag, keine Zahlung. Die Texte stehen
 * hier, damit Preiszeile, Annahmebereich und feste Leiste dieselben Wörter benutzen. Die Seite wählt
 * heute DE oder EN; IT und FR sind vollständig mitgeführt, damit eine spätere Sprachwahl nichts
 * nachziehen muss (Test: `a-la-carte-texte.test.ts` prüft, dass jede Sprache jeden Schlüssel hat).
 *
 * Bewusst KEIN Wort „zahlungspflichtig“ und kein Zahlungsbedingungen-Verweis: es gibt nichts zu zahlen.
 */
export const A_LA_CARTE_GAST = {
  de: {
    preisZeile: "Preis nach Karte, Abrechnung im Restaurant",
    nachKarte: "Nach Karte",
    summePreis: "Preis",
    keineAnzahlung: "Es ist keine Anzahlung nötig. Mit Ihrer Zusage reservieren wir den Tisch verbindlich.",
    cta: "Verbindlich reservieren",
    agbLabel: "Ich nehme dieses Angebot verbindlich an.",
    agbLabelMitAgb: "Ich nehme dieses Angebot verbindlich an und akzeptiere die Allgemeinen Geschäftsbedingungen.",
  },
  en: {
    preisZeile: "Priced à la carte, settled at the restaurant",
    nachKarte: "À la carte",
    summePreis: "Price",
    keineAnzahlung: "No deposit is required. With your confirmation we reserve the table for you bindingly.",
    cta: "Reserve bindingly",
    agbLabel: "I bindingly accept this offer.",
    agbLabelMitAgb: "I bindingly accept this offer and agree to the general terms and conditions.",
  },
  it: {
    preisZeile: "Prezzo alla carta, pagamento al ristorante",
    nachKarte: "Alla carta",
    summePreis: "Prezzo",
    keineAnzahlung: "Non è richiesto alcun acconto. Con la Sua conferma riserviamo il tavolo in modo vincolante.",
    cta: "Prenotare in modo vincolante",
    agbLabel: "Accetto questa offerta in modo vincolante.",
    agbLabelMitAgb: "Accetto questa offerta in modo vincolante e accetto le condizioni generali di contratto.",
  },
  fr: {
    preisZeile: "Prix à la carte, règlement au restaurant",
    nachKarte: "À la carte",
    summePreis: "Prix",
    keineAnzahlung: "Aucun acompte n’est nécessaire. Avec votre accord, nous réservons la table de façon ferme.",
    cta: "Réserver fermement",
    agbLabel: "J’accepte cette offre de façon ferme.",
    agbLabelMitAgb: "J’accepte cette offre de façon ferme et j’accepte les conditions générales.",
  },
} as const satisfies Record<ALaCarteSprache, Record<string, string>>;
